import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {  Box,  Typography,  Paper,  Button,  CircularProgress,  Alert,  Dialog,  DialogTitle,  DialogContent,  DialogActions,  TextField,  IconButton,  Select,  MenuItem,  Chip,   Stack,
  alpha,  Snackbar,} from '@mui/material';
import { styled } from '@mui/material/styles'; 
import {  DndContext,  closestCenter,  KeyboardSensor,  PointerSensor,  useSensor,  useSensors,  DragOverlay,  MeasuringStrategy,} from '@dnd-kit/core';import {  arrayMove,
  SortableContext,  sortableKeyboardCoordinates,  verticalListSortingStrategy,} from '@dnd-kit/sortable';import { SortableItem } from './SortableItem';
import api from '../api/api';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import PestControlIcon from '@mui/icons-material/PestControl';
import OpacityIcon from '@mui/icons-material/Opacity';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import ShowerIcon from '@mui/icons-material/Shower';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import CatalogoForm from './CatalogoForm';
import { Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon } from '@mui/icons-material';

// *** ESTILOS MEJORADOS ***
dayjs.locale('es');

// Tarjeta personalizada con mejor estilo
const StyledCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'dragging' && prop !== 'isOverlay',
})(({ theme, dragging, isOverlay }) => ({
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
  backgroundColor: '#fff',
  borderRadius: 12,
  boxShadow: dragging || isOverlay 
    ? '0 10px 25px rgba(0,0,0,0.15)' 
    : '0 2px 8px rgba(0,0,0,0.08)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'grab',
  '&:hover': {
    boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
    transform: 'translateY(-2px)',
    borderColor: alpha(theme.palette.primary.main, 0.3),
  },
  '&:active': {
    cursor: 'grabbing',
  },
}));

const ColumnContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'active',
})(({ theme, isActive, active }) => ({ // Recibe ambas por si acaso
  flex: 1,
  minWidth: 300,
  transition: 'all 0.3s ease',
  '& .column-paper': {
    // Usamos 'isActive' o 'active' para el estilo visual
    backgroundColor: (isActive || active) ? alpha(theme.palette.primary.main, 0.08) : '#fafafa',
    border: (isActive || active) ? `2px dashed ${theme.palette.primary.main}` : '2px solid transparent',
    borderRadius: 16,
    height: '100%',
    minHeight: 500,
    padding: theme.spacing(2),
    overflowY: 'auto',
  },
}));

const EmptyPlaceholder = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(6),
  color: theme.palette.text.secondary,
  '& .icon': {
    fontSize: 48,
    opacity: 0.3,
    marginBottom: theme.spacing(2),
  },
}));

// *** LÓGICA DE CATEGORÍAS ***
export const categoryLabels = {
  VAC: {
    title: 'Vacunas',
    disponible: 'Vacunas Disponibles',
    conCita: 'Vacuna (Cita Próxima)',
    aplicada: 'Vacuna (Aplicada efectiva)',
    razon: 'Aplicación',
    icon: <VaccinesIcon sx={{ fontSize: 24 }} />,
  },
  DES: {
    title: 'Desparasitantes',
    disponible: 'Desparasitantes Disponibles',
    conCita: 'Desparasitante (Cita Próxima)',
    aplicada: 'Desparasitante (Aplicado efectivo)',
    razon: 'Desparasitación',
    icon: <PestControlIcon sx={{ fontSize: 24 }} />,
  },
  PIP: {
    title: 'Pipetas',
    disponible: 'Pipetas Disponibles',
    conCita: 'Pipeta (Cita Próxima)',
    aplicada: 'Pipeta (Aplicada efectiva)',
    razon: 'Aplicación de Pipeta',
    icon: <OpacityIcon sx={{ fontSize: 24 }} />,
  },
  CIR: {
    title: 'Cirugías',
    disponible: 'Cirugías Disponibles',
    conCita: 'Cirugía (Cita Próxima)',
    aplicada: 'Cirugía (Realizada efectiva)',
    razon: 'Cirugía',
    icon: <HealthAndSafetyIcon sx={{ fontSize: 24 }} />,
  },
  BAN: {
    title: 'Baños',
    disponible: 'Baños Disponibles',
    conCita: 'Baño (Cita Próxima)',
    aplicada: 'Baño (Realizado efectivo)',
    razon: 'Baño',
    icon: <ShowerIcon sx={{ fontSize: 24 }} />,
  },
  TER: {
    title: 'Terapias',
    disponible: 'Terapias Disponibles',
    conCita: 'Terapia (Cita Próxima)',
    aplicada: 'Terapia (Realizada efectiva)',
    razon: 'Terapia',
    icon: <MedicalServicesIcon sx={{ fontSize: 24 }} />,
  },
};


const GestionCitasPaciente = ({ visible, onClose, id_paciente, id_sucursal, tabCategoria, nombre_paciente }) => {
  // *** ESTADO Y HOOKS ***
  const [itemsDisponibles, setItemsDisponibles] = useState([]);
  const [itemsConCita, setItemsConCita] = useState([]);
  const [itemsAplicados, setItemsAplicados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCatalogoForm, setOpenCatalogoForm] = useState(false);
  const [mode, setMode] = useState(null);
  const [selectedCatalogoId, setSelectedCatalogoId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedEstado, setSelectedEstado] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [activeDropArea, setActiveDropArea] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sucursales, setSucursales] = useState([]);
  const [selectedSucursal, setSelectedSucursal] = useState(id_sucursal);
  const [isMaximized, setIsMaximized] = useState(false); 
  
  // *** NUEVO ESTADO PARA CONFIRMACIÓN DE ELIMINACIÓN ***
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [loteInput, setLoteInput] = useState(''); // Nuevo estado para número de lote
  const [isUpdate, setIsUpdate] = useState(false);


  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');


  const labels = categoryLabels[tabCategoria] || categoryLabels['VAC'];
  const editableEstados = ['PRG', 'RRG', 'PEN', 'CAN'];
  

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // *** FUNCIONES AUXILIARES ***
  
  const cleanDateString = (dateStr) => {
    if (typeof dateStr === 'string' && dateStr.endsWith('Z')) {
      return dateStr.slice(0, -1);
    }
    return dateStr;
  };

  const getEstadoChip = (estado) => {
    const colors = {
      PRG: 'primary',
      RRG: 'info',
      PEN: 'warning',
      CAN: 'error',
      APL: 'success',
    };
    return <Chip label={estado} size="small" color={colors[estado] || 'default'} />;
  };

  // *** FETCH DATA Y EFECTOS ***
  
  useEffect(() => {
    const fetchSucursales = async () => {
      try {
        const response = await api.get('/sucursales/');
        setSucursales(response.data);
        setSelectedSucursal(id_sucursal); 
      } catch (error) {
        setError('Error al cargar sucursales: ' + (error.response?.data?.detail || error.message));
      }
    };
    if (visible) fetchSucursales();
  }, [visible, id_sucursal]);

  const fetchData = useCallback(async () => {
  if (!id_paciente) {
    setError('ID de paciente no proporcionado');
    setLoading(false);
    return;
  }
  try {
    setLoading(true);
    const catalogoRes = await api.get('/catalogos/', {
      params: { tab_categoria: tabCategoria },
    });

    const items = catalogoRes.data
      .filter(item => item.habilitado === true) 
      .map((item) => ({
        id: item.id_catalogo,
        descripcion: item.descripcion,
        tipo: 'disponible',
      }));
    setItemsDisponibles(items);

    const citasRes = await api.get(`/citas/tabs?id_paciente=${id_paciente}&id_tab=${tabCategoria}`);
    
    const itemsAplicadosData = citasRes.data.map((cita) => ({
      id: cita.id_cita,
      id_catalogo: cita.id_catalogo,
      descripcion: cita.descripcion,
      fecha_cita: cleanDateString(cita.fecha_cita) || null, 
      fecha_efectiva: cleanDateString(cita.fecha_efectiva) || null,
      tipo: cita.es_pendiente ? 'con_cita' : 'aplicada',
      estado_cita: cita.estado_cita || (cita.es_pendiente ? 'PRG' : 'APL'),
      razon_consulta: cita.razon_consulta || `${labels.razon}: ${cita.descripcion}`,
      lote: cita.lote_utilizado,
      id_sucursal: cita.id_sucursal,
    }));

    // --- LÓGICA DE ORDENAMIENTO COMPATIBLE ---

    // 1. Citas Próximas: De la fecha más futura a la más cercana (Descendente)
    const proximas = itemsAplicadosData
      .filter((v) => v.estado_cita !== 'APL')
      .sort((a, b) => dayjs(b.fecha_cita).diff(dayjs(a.fecha_cita)));

    // 2. Aplicadas: De la más reciente a la más antigua (Descendente)
    const aplicadas = itemsAplicadosData
      .filter((v) => v.estado_cita === 'APL')
      .sort((a, b) => dayjs(b.fecha_cita).diff(dayjs(a.fecha_cita)));

    setItemsConCita(proximas);
    setItemsAplicados(aplicadas);
    setLoading(false);
  } catch (error) {
    setError(`Error al cargar ${labels.title.toLowerCase()}: ` + (error.response?.data?.detail || error.message));
    setLoading(false);
  }
}, [id_paciente, tabCategoria, labels.title, labels.razon, setError, setLoading, setItemsAplicados, setItemsConCita, setItemsDisponibles]);
  // Nota: `api` y `cleanDateString` no necesitan ir si son estáticos

  useEffect(() => {
    if (visible) fetchData();
}, [visible, fetchData]);

 


  const handleDragEnd = async (event) => {
  setActiveId(null);
  setActiveDropArea(null);
  const { active, over } = event;
  if (!over) return;

  const activeId = active.id;

  // Determinar lista de origen
  let activeList = null;
  if (itemsDisponibles.some(v => v.id === activeId)) activeList = 'disponibles';
  else if (itemsConCita.some(v => v.id === activeId)) activeList = 'con_cita';
  else if (itemsAplicados.some(v => v.id === activeId)) activeList = 'aplicada';

  if (!activeList) return;

  // Determinar lista de destino
  const overList = over.data.current?.type || (over.id === 'placeholder-con-cita' ? 'con_cita' : over.id === 'placeholder-aplicada' ? 'aplicada' : null);
  if (!overList) return;

  // Si es el mismo contenedor, reordenar
  if (activeList === overList) {
    let items = [];
    if (activeList === 'disponibles') items = itemsDisponibles;
    else if (activeList === 'con_cita') items = itemsConCita;
    else if (activeList === 'aplicada') items = itemsAplicados;

    const oldIndex = items.findIndex(v => v.id === activeId);
    const newIndex = over.id === `placeholder-${activeList}` ? 0 : items.findIndex(v => v.id === over.id);

    if (oldIndex !== newIndex) {
      const newItems = arrayMove(items, oldIndex, newIndex);
      if (activeList === 'disponibles') setItemsDisponibles(newItems);
      else if (activeList === 'con_cita') setItemsConCita(newItems);
      else if (activeList === 'aplicada') setItemsAplicados(newItems);
    }
    return;
  }

  // Manejar drags desde disponibles
  if (activeList === 'disponibles') {
    const item = itemsDisponibles.find(v => v.id === activeId);
    if (!item) return;

    setSelectedItem(item);
    setActionType(overList === 'con_cita' ? 'PRG' : 'APL');
    setIsUpdate(false);  // Nuevo: siempre false para nuevos
    setSelectedDate(dayjs());
    setSelectedSucursal(id_sucursal);
    setLoteInput('');
    setOpenDatePicker(true);
    return;
  }

  // Manejar drags entre con_cita y aplicada
  if (activeList === 'con_cita' || activeList === 'aplicada') {
    const item = activeList === 'con_cita'
      ? itemsConCita.find(v => v.id === activeId)
      : itemsAplicados.find(v => v.id === activeId);

    if (!item) return;

    if (activeList === 'con_cita' && overList === 'aplicada') {
      // Abrir diálogo para APL (update existente)
      setSelectedItem(item);
      setActionType('APL');
      setIsUpdate(true);  // Clave: true para PUT
      setSelectedDate(dayjs(item.fecha_cita || new Date()));
      setSelectedSucursal(item.id_sucursal || id_sucursal);
      setLoteInput(item.lote || '');
      setOpenDatePicker(true);
      return;
    } else if (activeList === 'aplicada' && overList === 'con_cita') {
      // Revertir a PRG sin diálogo
      try {
        const payload = {
          estado_cita: 'PRG',
          razon_consulta: `${labels.razon}: ${item.descripcion}`,
          fecha_cita: item.fecha_cita,
        };
        await api.put(`/citas/${item.id}`, payload);

        const updatedItem = { ...item, tipo: 'con_cita', estado_cita: 'PRG' };
        setItemsAplicados(prev => prev.filter(v => v.id !== activeId));
        setItemsConCita(prev => [...prev, updatedItem]);
      } catch (error) {
        setError('Error al revertir: ' + (error.response?.data?.detail || error.message));
      }
    }
  }
};

  // *** LÓGICA DE ELIMINACIÓN MODIFICADA ***
  
  // Función que realmente ejecuta la eliminación (usada internamente por handleRemoveCita y el modal)
 const confirmRemoveCita = async (item) => {
  if (!item) return;
  try {
    await api.delete(`/citas/${item.id}`);
    setSnackbarMessage('Cita eliminada correctamente');
    setSnackbarSeverity('success');
  } catch (error) {
    if (error.response?.status === 404) {
      setSnackbarMessage('Cita ya eliminada, actualizando vista');
      setSnackbarSeverity('info');
    } else {
      setSnackbarMessage('Error al eliminar: ' + (error.response?.data?.detail || error.message));
      setSnackbarSeverity('error');
    }
  } finally {
    // Limpieza de UI siempre, sin importar el resultado
    setItemsAplicados(prev => prev.filter(v => v.id !== item.id));
    setItemsConCita(prev => prev.filter(v => v.id !== item.id));
    setOpenConfirmDelete(false);
    setItemToDelete(null);
    setSnackbarOpen(true);
  }
};


  const handleCloseConfirmDelete = () => {
    setOpenConfirmDelete(false);
    setItemToDelete(null);
  };
  
  // *** RESTO DE MANEJADORES (Sin cambios) ***

  const handleOpenAction = (item, type) => {
    setSelectedItem(item);
    setActionType(type);
    setOpenDatePicker(true);
  };

  const handleEditCita = (item) => {
    setSelectedItem(item);
    setSelectedEstado(item.estado_cita);
    setSelectedDate(item.fecha_cita ? dayjs(cleanDateString(item.fecha_cita)) : dayjs()); 
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedItem(null);
    setSelectedEstado(null);
  };

  const handleSaveEdit = async () => {
    if (!selectedItem || !selectedEstado) return;
    try {
      const payload = {
        estado_cita: selectedEstado,
        fecha_cita: selectedDate.format('YYYY-MM-DDTHH:mm:ss'), 
        razon_consulta: `${labels.razon}: ${selectedItem.descripcion}`,
      };
      
      const res = await api.put(`/citas/${selectedItem.id}`, payload);
      const updatedCita = res.data;

      const updatedItem = {
        ...selectedItem,
        estado_cita: updatedCita.estado_cita,
        fecha_cita: cleanDateString(updatedCita.fecha_cita), 
        razon_consulta: updatedCita.razon_consulta,
      };

      if (updatedCita.estado_cita === 'APL') {
        setItemsConCita(prevItems => prevItems.filter((v) => v.id !== selectedItem.id));
        setItemsAplicados(prevItems => [...prevItems, updatedItem]);
      } else {
        setItemsConCita(prevItems => prevItems.map((v) => (v.id === selectedItem.id ? updatedItem : v)));
        setItemsAplicados(prevItems => prevItems.filter((v) => v.id !== selectedItem.id));
      }
      setOpenEditDialog(false);
      setSelectedItem(null);
      setSelectedEstado(null);
    } catch (error) {
      setError('Error al actualizar cita: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCloseDatePicker = () => {
    setOpenDatePicker(false);
    setSelectedSucursal(id_sucursal);
    setSelectedItem(null);
    setActionType(null);
  };

  const handleSaveDate = async () => {
  if (!selectedItem || !actionType || !selectedSucursal) return;
  if (actionType === 'APL' && !loteInput.trim()) {
    setError('Lote obligatorio para APL');
    return;
  }

  const razonConsulta = `${labels.razon}: ${selectedItem.descripcion}`;
  const fechaISO = selectedDate.format('YYYY-MM-DD HH:mm:ss');

  try {
    let updatedItem;
    if (isUpdate) {
      // UPDATE (para drags existentes a APL)
      const payload = {
        estado_cita: 'APL',
        razon_consulta: razonConsulta,
        fecha_cita: fechaISO,
        id_sucursal: selectedSucursal,  // Actualiza si cambió
        lote_utilizado: loteInput.trim(),
      };
      const res = await api.put(`/citas/${selectedItem.id}`, payload);  // id es id_cita
      updatedItem = {
        ...selectedItem,
        fecha_cita: cleanDateString(res.data.fecha_cita),
        id_sucursal: res.data.id_sucursal,
        lote: res.data.lote_utilizado,
        estado_cita: 'APL',
        tipo: 'aplicada',
        razon_consulta: res.data.razon_consulta,
      };
      setItemsConCita(prev => prev.filter(v => v.id !== selectedItem.id));
      setItemsAplicados(prev => [...prev, updatedItem]);
    } else {
      // CREATE (lógica original para nuevos)
      const payload = {
        id_paciente,
        id_catalogo: selectedItem.id,  // id es id_catalogo
        id_sucursal: selectedSucursal,
        estado_cita: actionType,
        fecha_cita: fechaISO,
        ...(actionType === 'APL' && { 
        fecha_efectiva: fechaISO,
        es_pendiente: false // Forzamos el estado explícitamente en el envío
        }),
        razon_consulta: razonConsulta,
        descripcion: selectedItem.descripcion,
        ...(actionType === 'APL' && { lote_utilizado: loteInput.trim() }),
      };
      const res = await api.post(`/citas/${id_paciente}/simple`, payload);
      updatedItem = {
        id: res.data.id_cita,
        id_catalogo: res.data.id_catalogo,
        descripcion: res.data.descripcion,
        fecha_cita: cleanDateString(res.data.fecha_cita),
        fecha_efectiva: cleanDateString(res.data.fecha_efectiva),
        tipo: actionType === 'PRG' ? 'con_cita' : 'aplicada',
        estado_cita: actionType,
        razon_consulta: res.data.razon_consulta,
        id_sucursal: res.data.id_sucursal,
        lote: actionType === 'APL' ? loteInput.trim() : undefined,
      };
      if (actionType === 'PRG') {
        setItemsConCita(prev => [...prev, updatedItem]);
      } else {
        setItemsAplicados(prev => [...prev, updatedItem]);
      }
    }

    setOpenDatePicker(false);
    setSelectedItem(null);
    setActionType(null);
    setLoteInput('');
    setIsUpdate(false);
    setSelectedSucursal(id_sucursal);
  } catch (error) {
    setError('Error al guardar: ' + (error.response?.data?.detail || error.message));
  }
};

  const getDragOverlayContent = () => {
    const item =
      itemsDisponibles.find((v) => v.id === activeId) ||
      itemsConCita.find((v) => v.id === activeId) ||
      itemsAplicados.find((v) => v.id === activeId);

    if (!item) return null;

    return (
      <StyledCard isOverlay>
        <Typography fontWeight="medium">{item.descripcion}</Typography>
      </StyledCard>
    );
  };

 const filteredDisponibles = useMemo(() =>
    itemsDisponibles.filter((item) => {
      const descripcion = item.descripcion || '';
      const busqueda = searchQuery || '';
      return descripcion.toLowerCase().includes(busqueda.toLowerCase());
    }),
  [itemsDisponibles, searchQuery]
);

  const handleOpenCatalogoForm = (formMode, catalogoId = null) => {
    setMode(formMode);
    setSelectedCatalogoId(catalogoId);
    setOpenCatalogoForm(true);
  };

  // *** RENDERIZADO *** fuerza selector a adapterLocale="es">
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es"> 
      <Box sx={{
        p: { xs: 2, md: 3 },
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', 
        borderRadius: 3,
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
        position: isMaximized ? 'fixed' : 'relative',
        inset: isMaximized ? 0 : 'auto',
        zIndex: 800,
        overflow: 'hidden',
        height: isMaximized ? '100vh' : 'auto', 
      }}>
        {/* Header mejorado */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ color: '#1976d2' }}>
              {labels.icon}
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {labels.title} • {nombre_paciente}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Arrastra para programar o aplicar
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={() => setIsMaximized(!isMaximized)} 
            size="large"
            sx={{ 
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': { bgcolor: 'background.default' }
            }}
          >
            {isMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={60} thickness={5} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 3, fontSize: '1.1rem' }}>
            {error}
          </Alert>
        ) : (
          <DndContext
  sensors={sensors}
  collisionDetection={closestCenter}
  onDragStart={(e) => setActiveId(e.active.id)}
  onDragOver={(e) => {
    if (!e.over) {
      setActiveDropArea(null);
      return;
    }

    const overId = e.over.id;
    const overDataType = e.over.data?.current?.type;

    let dropArea = null;
    if (overDataType) {
      dropArea = overDataType; // desde data={{ type: 'aplicada' }}
    } else if (typeof overId === 'string' && overId.includes('placeholder-')) {
      dropArea = overId.split('placeholder-')[1]; // placeholder vacío
    }

    setActiveDropArea(dropArea);
  }}
  onDragEnd={handleDragEnd}
  onDragCancel={() => {
    setActiveId(null);
    setActiveDropArea(null);
  }}
  measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
>
            <Stack 
              direction={{ xs: 'column', md: 'row' }}
              spacing={3}
              sx={{ height: isMaximized ? 'calc(100vh - 180px)' : 'auto' }} 
            >
              {/* Columna Disponibles */}
              <ColumnContainer isActive={activeDropArea === 'disponibles'}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    {labels.disponible}
                  </Typography>
                  <Button 
                    variant="contained" 
                    size="small"
                    onClick={() => handleOpenCatalogoForm('add')}
                  >
                    + Agregar
                  </Button>
                </Box>

                <TextField
                  placeholder={`Buscar en ${labels.title.toLowerCase()}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                />

                <Box className="column-paper">
                  <SortableContext items={filteredDisponibles.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    {filteredDisponibles.length === 0 ? (
                      <EmptyPlaceholder>
                        {labels.icon}
                        <Typography>No hay ítems disponibles</Typography>
                      </EmptyPlaceholder>
                    ) : (
                      filteredDisponibles.map((item) => (
                        <StyledCard 
                          key={item.id} 
                          id={item.id} 
                          
                        >
                          <SortableItem id={item.id} data={{ type: 'disponibles' }} component={StyledCard} >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography fontWeight="medium">{item.descripcion}</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <IconButton size="small" color="primary" onClick={() => handleOpenAction(item, 'PRG')}>
                                    <ScheduleIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" color="success" onClick={() => handleOpenAction(item, 'APL')}>
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" color="error" onClick={() => handleOpenCatalogoForm('disable', item.id)}>
                                    <DoNotDisturbIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                          </SortableItem>
                        </StyledCard>
                      ))
                    )}
                  </SortableContext>
                </Box>
              </ColumnContainer>

              {/* Columna Con Cita */}
              <ColumnContainer isActive={activeDropArea === 'con_cita'}>
                <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                  {labels.conCita}
                </Typography>
                <Box className="column-paper">
                  <SortableContext items={itemsConCita.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    {itemsConCita.length === 0 ? (
                      <EmptyPlaceholder id="placeholder-con-cita" data={{ type: 'con_cita' }}>
                        <ScheduleIcon className="icon" />
                        <Typography variant="h6" component="div" color="text.disabled"> 
                          Arrastra aquí para programar
                        </Typography>
                      </EmptyPlaceholder>
                    ) : (
                      itemsConCita.map((item) => (
                        <StyledCard key={item.id} >
                          <SortableItem id={item.id} data={{ type: 'con_cita' }} >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography fontWeight="600">{item.descripcion}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {item.fecha_cita ? dayjs(cleanDateString(item.fecha_cita)).format('D [de] MMMM [de] YYYY'): 'Sin fecha programada'}
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                  {getEstadoChip(item.estado_cita)}
                                </Box>
                              </Box>
                              <Box sx={{ ml: 1 }}>
                                {editableEstados.includes(item.estado_cita) && (
                                  <IconButton size="small" onClick={() => handleEditCita(item)} title="Editar cita">
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                )}
                                <IconButton   size="small"   color="error"  onClick={(e) => {    e.stopPropagation();  e.preventDefault();     setItemToDelete(item);
                                    setOpenConfirmDelete(true);
                                  }}
                                  title="Eliminar Cita Próxima">
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          </SortableItem>
                        </StyledCard>
                      ))
                    )}
                  </SortableContext>
                </Box>
              </ColumnContainer>

              {/* Columna Aplicadas */}
              <ColumnContainer isActive={activeDropArea === 'aplicada'}>
                <Typography variant="h6" fontWeight="bold" color="warning.main" sx={{ mb: 2 }}>
                  {labels.aplicada}
                </Typography>
                <Box className="column-paper">
                  <SortableContext items={itemsAplicados.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    {itemsAplicados.length === 0 ? (
                      <EmptyPlaceholder id="placeholder-aplicada" data={{ type: 'aplicada' }}>
                        <CheckCircleIcon className="icon" />
                        <Typography variant="h6" color="text.disabled">
                          Arrastra aquí para marcar como aplicada
                        </Typography>
                      </EmptyPlaceholder>
                    ) : (
                      itemsAplicados.map((item) => (
                        <StyledCard key={item.id} >
                          <SortableItem id={item.id} data={{ type: 'aplicada' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography fontWeight="600">{item.descripcion}</Typography>
                                <Typography variant="body2" component="span" color="success.main">  
                                  Aplicada el {item.fecha_cita ? dayjs(cleanDateString(item.fecha_cita)).format('D MMMM YYYY [-] h:mm A'): 'Sin fecha'}
                                  {item.lote && (
                                    <Chip 
                                      label={`Lote: ${item.lote}`} 
                                      size="small" 
                                      color="success" 
                                      variant="outlined"
                                      sx={{ ml: 1, fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Typography>
                              </Box>
                              {/* Llama a handleRemoveCita, que abrirá el modal para este tipo de ítem */}
                              <IconButton   size="small"   color="error"  onClick={(e) => {    e.stopPropagation();    e.preventDefault();  
                                    setItemToDelete(item);    setOpenConfirmDelete(true);  }}
                                  title="Eliminar Aplicada efectiva" >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                            </Box>
                          </SortableItem>
                        </StyledCard>
                      ))
                    )}
                  </SortableContext>
                </Box>
              </ColumnContainer>
            </Stack>

            <DragOverlay>{getDragOverlayContent()}</DragOverlay>
          </DndContext>
        )}

        {/* Botón cerrar mejorado */}
        <Box sx={{ mt: 4, textAlign: 'right' }}>
          <Button onClick={onClose} variant="outlined" size="large">
            Cerrar Panel
          </Button>
        </Box>

        {/* Diálogo de Programación/Aplicación de Cita (DateTimePicker) */}
        <Dialog open={openDatePicker} onClose={handleCloseDatePicker} maxWidth="sm" fullWidth>
          <DialogTitle>
            {actionType === 'PRG' 
              ? `Programar: ${labels.conCita}`
              : `Registrar Aplicación: ${labels.aplicada}`
            }
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {selectedItem?.descripcion}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Select
              value={selectedSucursal || ''}
              onChange={(e) => setSelectedSucursal(e.target.value)}
              fullWidth
              displayEmpty
              sx={{ mb: 2 }}
            >
              <MenuItem value="" disabled>Seleccionar sucursal</MenuItem>
              {sucursales.map((sucursal) => (
                <MenuItem key={sucursal.id_sucursal} value={sucursal.id_sucursal}>
                  {sucursal.nombre_sucursal}
                </MenuItem>
              ))}
            </Select>

            <DateTimePicker
              label="Fecha y Hora de Aplicación"
              value={selectedDate}
              onChange={setSelectedDate}
              format="D [de] MMMM [de] YYYY, h:mm A"
              ampm={true}
              minutesStep={30}
              localeText={{ okButtonLabel: 'Aceptar', cancelButtonLabel: 'Cancelar' }}
              slotProps={{
                textField: { fullWidth: true, sx: { mb: 2 } },
                popper: { placement: 'bottom-start' },
              }}
            />

            {/* CAMPO LOTE: solo visible cuando se está aplicando (no programando) */}
            {actionType === 'APL' && (
              <TextField
                label="Número de Lote (obligatorio al aplicar)"
                value={loteInput}
                onChange={(e) => setLoteInput(e.target.value)}
                fullWidth
                required
                placeholder="Ej: LOT-2025-ABC123"
                helperText="Este dato es obligatorio para registrar la aplicación efectiva"
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDatePicker}>Cancelar</Button>
            <Button 
              onClick={handleSaveDate} 
              variant="contained"
              disabled={!selectedSucursal || (actionType === 'APL' && !loteInput.trim())}
            >
              {actionType === 'PRG' ? 'Programar Cita' : 'Registrar Aplicación'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Diálogo de Edición de Cita */}
        <Dialog open={openEditDialog} onClose={handleCloseEditDialog}>
          <DialogTitle>Editar Cita</DialogTitle>
          <DialogContent>
            <Select value={selectedEstado || ''} onChange={(e) => setSelectedEstado(e.target.value)} fullWidth label="Estado de la cita" sx={{ mb: 2 }}>
              {editableEstados.map((estado) => (
                <MenuItem key={estado} value={estado}>
                  {estado}
                </MenuItem>
              ))}
            </Select>
            <DateTimePicker
                label="Fecha y Hora"
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                format="D [de] MMMM [de] YYYY, h:mm A"
                ampm={true}
                minutesStep={30}
                slotProps={{ textField: { fullWidth: true } }}
              />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog} color="secondary">
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} color="primary">
              Guardar
            </Button>
          </DialogActions>
        </Dialog>

        {/* --- NUEVO DIÁLOGO DE CONFIRMACIÓN DE ELIMINACIÓN --- */}
        <Dialog open={openConfirmDelete} onClose={handleCloseConfirmDelete}>
          <DialogTitle>Confirmar Eliminación de Registro</DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              ¿Estás seguro que deseas **eliminar el registro de aplicación efectiva** de:
              <br/>
              **{itemToDelete?.descripcion}**?
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              Esta acción es irreversible y eliminará el historial de aplicación.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirmDelete} color="secondary" variant="outlined">
              Cancelar
            </Button>
            <Button 
              onClick={() => confirmRemoveCita(itemToDelete)} 
              color="error" 
              variant="contained" 
              disabled={!itemToDelete}
            >
              Eliminar Permanentemente
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Formulario de Catálogo */}
        <CatalogoForm
          open={openCatalogoForm}
          onClose={() => setOpenCatalogoForm(false)}
          mode={mode}
          tabCategoria={tabCategoria}
          id_catalogo={selectedCatalogoId}
          onSave={fetchData}
        />

<Snackbar
  open={snackbarOpen}
  autoHideDuration={6000}
  onClose={() => setSnackbarOpen(false)}
  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
>
  <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
    {snackbarMessage}
  </Alert>
</Snackbar>


      </Box>
    </LocalizationProvider>
  );
};

export default GestionCitasPaciente;